import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  getStudentQuizWorkspace,
  postStudentQuizAttempt,
  type StudentQuizReviewItem,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { QuizRichText } from "../components/QuizRichText";
import { celebratePassing } from "../student-ide/confettiCelebration";
import { QuizIcon } from "../teacher/QuizIcon";

export default function StudentQuizPage() {
  const [searchParams] = useSearchParams();
  const cohortId = searchParams.get("cohortId") ?? "";
  const quizId = searchParams.get("quizId") ?? "";
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState<Awaited<ReturnType<typeof getStudentQuizWorkspace>> | null>(null);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<{
    scorePercent: number;
    review: StudentQuizReviewItem[];
    attemptNumber: number;
    bestScorePercent: number;
    attempt1Score: number | null;
    attempt2Score: number | null;
    retakeAvailable: boolean;
    retakeUsed: boolean;
  } | null>(null);

  const load = useCallback(
    async (opts?: { startRetake?: boolean }) => {
      if (!bearer || !cohortId || !quizId) return;
      setLoading(true);
      setError(null);
      try {
        const ws = await getStudentQuizWorkspace(bearer, cohortId, quizId, opts);
        setWorkspace(ws);
        setAnswers({});
        if (!ws.activeQuestions.length && ws.review) {
          setLastResult({
            scorePercent: ws.bestScorePercent ?? 0,
            review: ws.review,
            attemptNumber: ws.retakeUsed ? 2 : 1,
            bestScorePercent: ws.bestScorePercent ?? 0,
            attempt1Score: ws.attempt1Score,
            attempt2Score: ws.attempt2Score,
            retakeAvailable: ws.retakeAvailable,
            retakeUsed: ws.retakeUsed,
          });
        } else if (ws.activeQuestions.length > 0) {
          setLastResult(null);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not load quiz");
      } finally {
        setLoading(false);
      }
    },
    [bearer, cohortId, quizId]
  );

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSubmit() {
    if (!bearer || !workspace?.activeAttemptNumber) return;
    const responses = (workspace.activeQuestions ?? []).map((q) => ({
      questionId: q.id,
      selectedOptionIndex: answers[q.id] ?? -1,
    }));
    if (responses.some((r) => r.selectedOptionIndex < 0)) {
      setError("Answer every question before submitting.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await postStudentQuizAttempt(bearer, cohortId, quizId, {
        attemptNumber: workspace.activeAttemptNumber,
        responses,
      });
      setLastResult({
        scorePercent: result.scorePercent,
        review: result.review,
        attemptNumber: result.attemptNumber,
        bestScorePercent: result.bestScorePercent,
        attempt1Score: result.attempt1Score,
        attempt2Score: result.attempt2Score,
        retakeAvailable: result.retakeAvailable,
        retakeUsed: result.retakeUsed,
      });
      if (result.bestScorePercent === 100) {
        celebratePassing();
      }
      setWorkspace((ws) =>
        ws
          ? {
              ...ws,
              attemptCount: result.attemptNumber,
              bestScorePercent: result.bestScorePercent,
              attempt1Score: result.attempt1Score,
              attempt2Score: result.attempt2Score,
              retakeAvailable: result.retakeAvailable,
              retakeUsed: result.retakeUsed,
              activeAttemptNumber: null,
              activeQuestions: [],
              progressStatus: result.retakeUsed || result.scorePercent === 100 ? "complete" : "in_progress",
            }
          : ws
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (!cohortId || !quizId) {
    return <p className="student-panel-error">Missing cohort or quiz.</p>;
  }

  if (loading) return <p className="student-panel-muted">Loading quiz…</p>;
  if (error && !workspace) return <p className="student-panel-error">{error}</p>;
  if (!workspace) return null;

  const showForm = workspace.open && workspace.activeQuestions.length > 0 && !lastResult;

  return (
    <div className="student-quiz-page teacher-pad">
      <header className="student-quiz-head">
        <QuizIcon
          quizId={workspace.quiz.id}
          title={workspace.quiz.title}
          iconName={workspace.quiz.iconName}
          bgColor={workspace.quiz.iconBgColor}
          fgColor={workspace.quiz.iconFgColor}
        />
        <div>
          <h1 className="student-quiz-title">{workspace.quiz.title}</h1>
          {workspace.quiz.description ? (
            <p className="student-quiz-lede">{workspace.quiz.description}</p>
          ) : null}
        </div>
      </header>

      {!workspace.open && !workspace.released ? (
        <p className="student-panel-muted">This quiz is not available yet.</p>
      ) : null}

      {error ? <p className="student-panel-error">{error}</p> : null}

      {showForm ? (
        <form
          className="student-quiz-form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          {workspace.activeQuestions.map((q, idx) => (
            <fieldset key={q.id} className="student-quiz-question">
              <legend className="visually-hidden">Question {idx + 1}</legend>
              <div className="student-quiz-question-prompt">
                <span className="student-quiz-question-num" aria-hidden>
                  {idx + 1}.
                </span>
                <QuizRichText text={q.prompt} />
              </div>
              {q.options.map((opt, optIdx) => (
                <label key={optIdx} className="student-quiz-option">
                  <input
                    type="radio"
                    name={q.id}
                    checked={answers[q.id] === optIdx}
                    onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: optIdx }))}
                  />
                  <QuizRichText text={opt} />
                </label>
              ))}
            </fieldset>
          ))}
          <button type="submit" className="btn primary" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit answers"}
          </button>
        </form>
      ) : null}

      {lastResult ? (
        <section className="student-quiz-results">
          <h2>
            {lastResult.attemptNumber === 2 ? "Retake score" : "Your score"}: {lastResult.scorePercent}%
          </h2>
          {lastResult.retakeUsed ? (
            <p className="student-quiz-score-summary">
              First try: {lastResult.attempt1Score ?? "—"}% · Retake: {lastResult.attempt2Score ?? "—"}% · Best:{" "}
              {lastResult.bestScorePercent}%
            </p>
          ) : lastResult.retakeAvailable ? (
            <p className="student-quiz-score-summary">
              This is your first attempt. Your best score counts if you retake.
            </p>
          ) : null}
          <ul className="student-quiz-review">
            {lastResult.review.map((item) => (
              <li
                key={item.questionId}
                className={item.wasCorrect ? "student-quiz-review--ok" : "student-quiz-review--wrong"}
              >
                <div className="student-quiz-review-prompt">
                  <QuizRichText text={item.prompt} />
                </div>
                <div>
                  Your answer:{" "}
                  {item.selectedOptionIndex >= 0 ? (
                    <QuizRichText text={item.options[item.selectedOptionIndex] ?? ""} />
                  ) : (
                    "(no answer)"
                  )}
                </div>
                {!item.wasCorrect ? <div className="student-quiz-review-flag">Incorrect</div> : null}
              </li>
            ))}
          </ul>
          {lastResult.retakeAvailable ? (
            <div className="student-quiz-retake-panel">
              <h3>Retake available</h3>
              <p>
                You may retake this quiz once. Some questions may be different. Your best score of the two attempts
                counts.
              </p>
              <div className="student-quiz-retake-actions">
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => void load({ startRetake: true })}
                >
                  Start retake
                </button>
                <Link className="btn secondary" to={`/cohorts/${encodeURIComponent(cohortId)}`}>
                  Keep this score
                </Link>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      <p className="student-quiz-back">
        <Link to={`/cohorts/${encodeURIComponent(cohortId)}`}>Back to class</Link>
      </p>
    </div>
  );
}
